<template>
  <span :class="setNegativeClass()" >
    <hy-input v-if="!readOnly" autocomplete="off" type="text" :class="{ 'STATUS_ERROR' : isNaN(value) }" v-bind="$attrs" v-model="formattedNumber" @blur="update" @beforeinput="checkBeforeInput" @paste="checkPaste" :placeholder="placeholder" reverse/>
    <span v-else-if="isNaN(value)" :class="$attrs.class">Error</span>
    <span v-else v-text="formattedNumber === '' ? nullDisplayValue : formattedNumber" :class="$attrs.class"></span>
  </span>
</template>

<script>
import { unref, watchEffect, shallowRef } from "vue";
import HyInput from "../../basic-components/HyInput.vue";
/*
  Erlaubt eingabe von formatierten deutschen Zahlen

  Beispiele für format - Property
    0,0  - Integer Formatierung mit Grouping -> 1.223.333
    undefined oder "" oder 0  - Integer Formatierung ohne Grouping -> 1223333
    0,0.00  - Float Formatierung mit 2 Nachkommastellen -> 1223333,12
    0,0.00##  - Float Formatierung mit 2 fixed Nachkommastellen mit maxmial 4 Nachkommastellen -> 333,0011 und 333,00

    vor dem Dezimaltrenner sind die nullen nicht wichtig es wird nur nach dem Komma gesucht um das Grouping zu steuern

*/
export function format(number, format){
  if(isNaN(number)){
    return number;
  }
  if (number === null || number === undefined) return "";
  let [integerPart, fractionPart] = format.split(".");
  let grouping = integerPart.indexOf(",") !== -1;
  let minimumFractionDigits = fractionPart?.match(/0/g)?.length ?? 0;
  let maximumFractionDigits = fractionPart?.match(/#/g)?.length ?? 0;
  maximumFractionDigits+=minimumFractionDigits;
  return Number(number).toLocaleString("de", {
    useGrouping: grouping,
    maximumFractionDigits: maximumFractionDigits,
    minimumFractionDigits: minimumFractionDigits
  });
}
    
export default {
  inheritAttrs:false,
  components: {
    HyInput
  },
  props:{
    "value": {
      type: [Number]
    },
    "format": {
      type: String,
      default: "0,0"
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    placeholder: String,
    nullDisplayValue: { type: String, default: "-" },
    allowNegativeValues: Boolean,
    forceMinValue: Number,
    setClassOnNegative: [Boolean, String]
  },
  emits:["update:value"],
  setup(props, context){
  
    function preventNonNumericBeforeInput(evt, key) {
      // preventXY liefern genau dann true zurück, wenn der neue key abgelehnt werden soll
      let preventDotInFraction = () => key === "." && evt.target.value.indexOf(",") !== -1 && evt.target.value.indexOf(",") < evt.target.selectionStart;
      let preventMultipleKomma = () => key === "," && /,/.test(formattedNumber.value);
      // "Minus" nur am Start und auch nur, wenn noch kein anderes Minus drinnen ist
      let preventMinusNotStart = () => key === "-" && (evt.target.selectionStart>0 || evt.target.value.indexOf("-")!==-1); 
      const regex = props.allowNegativeValues ? /[0-9,.-]/ : /[0-9,.]/
      if(key.length === 1 && 
         !(evt.altKey || evt.shiftKey || evt.ctrlKey) &&
         (!regex.test(key) || preventMultipleKomma() || preventDotInFraction() || preventMinusNotStart())){
        evt.preventDefault();
        return false;
      }
      return true;
    }



    function unformat(stringValue){
      let value = stringValue;
      let unrefed = unref(value);
      let trimmed = String(unrefed).trim();
      trimmed = trimmed.replaceAll(".","").replaceAll(",", ".");

      if (trimmed.length === 0 || value === undefined || value === null || trimmed==="-") {
        return {
          formatted: "",
          parsed: null
        }
      }
      
      let val = Number(trimmed);

      if (isNaN(val)){
        return {
          formatted: stringValue,
          parsed: stringValue
        }
      }

      let formattedValue = format(val, props.format) // formatieren zum Runden etc
      trimmed = formattedValue.replaceAll(".","").replaceAll(",", ".");
      val = Number(trimmed);

      return {
        formatted: formattedValue,
        parsed: val
      };
    }

    function update(evt){
      let unf = unformat(evt.target.value);
      if (props.forceMinValue && (unf.parsed == null || unf.parsed == undefined || unf.parsed < props.forceMinValue)) {
        formattedNumber.value = format(props.forceMinValue, props.format);
        context.emit("update:value", props.forceMinValue);
      }
      else {
        formattedNumber.value = unf.formatted;
        context.emit("update:value", unf.parsed);
      } 
    }

    function checkPaste(evt){
      let paste = (evt.clipboardData || window.clipboardData).getData('text');
      const el = evt.target
      const start = el.selectionStart
      const end = el.selectionEnd
      const text = el.value
      const before = text.substring(0, start)
      const after  = text.substring(end, text.length)
      const newval = (before + paste + after)
      let val = unformat(newval)
      if(isNaN(val.parsed)){
        evt.preventDefault();
      }
    }

    function checkBeforeInput(evt) {
      if (evt.data) {
        for ( const key of evt.data) {
          preventNonNumericBeforeInput(evt, key)
        }
        let paste = evt.data;
        const el = evt.target
        const start = el.selectionStart
        const end = el.selectionEnd
        const text = el.value
        const before = text.substring(0, start)
        const after = text.substring(end, text.length)
        const newval = (before + paste + after)
        let val = unformat(newval)
        if (isNaN(val.parsed)) {
          evt.preventDefault();
        }
      }
    }

    function formatValue(value){
      try{
        return format(unref(value), props.format);
      }
      catch(e){
        return unref(value);
      }
    }
    
    const formattedNumber = shallowRef(formatValue(props.value));
    watchEffect(() => {
      formattedNumber.value = formatValue(props.value);
    });

    function setNegativeClass() {
      let defaultClassName = 'negative';
      if (props.setClassOnNegative) {
        if (props.value < 0)
          return typeof props.setClassOnNegative === 'string' ? props.setClassOnNegative : defaultClassName;
      }
      return null;
    }
    
    return {
      formatValue,
      unformat,
      update,
      formattedNumber,
      checkPaste,
      checkBeforeInput,
      setNegativeClass
    }
  }
}
</script>

<style scoped="true">
input {
  text-align: right;
}
</style>